import { useEffect } from 'react'

/**
 * Calls refetch every pollInterval ms while isActive is true. The next poll is
 * scheduled after the previous refetch settles, so slow requests never overlap.
 *
 * Use this instead of useQuery's pollInterval for polling that is switched on
 * and off often (e.g. on page visibility). apollo-client 2.6 stopPollingQuery
 * drops the polling entry without clearing its pending timeout, so every
 * stop/start leaves an orphaned timer that fires an extra request once polling
 * restarts. Rapid tab switching then causes a burst of off-schedule polls.
 * Longer term, upgrading to @apollo/client 3.x (which clears the timeout when
 * polling stops) should fix this.
 * @param {function} refetch - useQuery's refetch
 * @param {number} pollInterval - in milliseconds; 0 disables polling
 * @param {boolean} isActive - false pauses polling and cancels the pending poll
 */
export const usePolling = (refetch, pollInterval, isActive) => {
  useEffect(() => {
    if (!isActive || !pollInterval) return undefined

    let isCancelled = false
    let timeoutId
    const scheduleNextPoll = () => {
      if (isCancelled) return
      timeoutId = setTimeout(() => {
        refetch()
          .catch(() => null)
          .then(scheduleNextPoll)
      }, pollInterval)
    }

    scheduleNextPoll()
    return () => {
      // also stops a refetch that is still in flight from scheduling another poll
      isCancelled = true
      clearTimeout(timeoutId)
    }
  }, [isActive, pollInterval, refetch])
}
